import type { ClassProfile, Student, TeamSet } from '../types';
import { uuid } from '../utils/helpers';

const PROFILES_KEY = 'rollpick:profiles';
const ACTIVE_KEY = 'rollpick:active';

export function getProfiles(): ClassProfile[] {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveProfile(profile: ClassProfile): void {
  const profiles = getProfiles();
  const idx = profiles.findIndex((p) => p.id === profile.id);
  if (idx >= 0) {
    profiles[idx] = { ...profile, updatedAt: Date.now() };
  } else {
    profiles.push(profile);
  }
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}

export function deleteProfile(id: string): void {
  const profiles = getProfiles().filter((p) => p.id !== id);
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
  if (getActiveProfileId() === id) {
    const next = profiles[0];
    localStorage.setItem(ACTIVE_KEY, next ? next.id : '');
  }
}

export function getActiveProfileId(): string | null {
  return localStorage.getItem(ACTIVE_KEY) || null;
}

export function setActiveProfileId(id: string): void {
  localStorage.setItem(ACTIVE_KEY, id);
}

export function getActiveProfile(): ClassProfile | null {
  const id = getActiveProfileId();
  if (!id) return null;
  return getProfiles().find((p) => p.id === id) ?? null;
}

export function createProfile(name: string, students: Student[]): ClassProfile {
  const profile: ClassProfile = {
    id: uuid(),
    name,
    students,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    teamHistory: [],
  };
  saveProfile(profile);
  setActiveProfileId(profile.id);
  return profile;
}

export function updateStudents(profileId: string, students: Student[]): void {
  const profiles = getProfiles();
  const p = profiles.find((p) => p.id === profileId);
  if (!p) return;
  p.students = students;
  p.updatedAt = Date.now();
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}

export function saveTeamSet(profileId: string, teamSet: TeamSet): void {
  const profiles = getProfiles();
  const p = profiles.find((p) => p.id === profileId);
  if (!p) return;
  p.teamHistory = p.teamHistory || [];
  p.teamHistory.unshift(teamSet);
  if (p.teamHistory.length > 20) p.teamHistory.length = 20;
  p.updatedAt = Date.now();
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}

export function incrementPickCount(profileId: string, studentIds: string[]): void {
  const profiles = getProfiles();
  const p = profiles.find((p) => p.id === profileId);
  if (!p) return;
  for (const s of p.students) {
    if (studentIds.includes(s.id)) {
      s.pickCount += 1;
    }
  }
  p.updatedAt = Date.now();
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}
